import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Package, MapPin, Clock, Eye, Share2, Phone, MessageCircle, 
  Mail, ChevronLeft, ChevronRight, Heart, Flag, User, Lock,
  Calendar, Tag, ExternalLink, Check, AlertCircle, Home, Ruler,
  Compass, Building, Key, IndianRupee, Shield, Droplet, Zap,
  Car, Trees, Dumbbell, Waves, Users, Baby, Send, Loader2,
  Sparkles, Star, BadgeCheck, Play, Image as ImageIcon, Copy
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Amenity icon mapping with enhanced icons
const amenityIcons = {
  lift: Building, security: Shield, cctv: Eye, gym: Dumbbell,
  swimming_pool: Waves, club_house: Building, garden: Trees,
  play_area: Baby, power_backup: Zap, water_supply: Droplet,
  gas_pipeline: Zap, fire_safety: Shield, intercom: Phone,
  parking: Car, ev_charging: Zap, jogging_track: Users,
  tennis: Users, basketball: Users, multipurpose_hall: Users,
  temple: Building, shopping: Package, atm: IndianRupee,
  cafeteria: Users, servant_room: Home, rainwater_harvesting: Droplet,
  solar: Zap, vastu_compliant: Compass, gated_community: Lock
};

// Floating particles animation component
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-primary/30 rounded-full animate-float"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${5 + Math.random() * 10}s`
        }}
      />
    ))}
  </div>
);

const ListingDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const imageContainerRef = useRef(null);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Lead Capture State
  const referrerCode = searchParams.get('ref') || '';
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    interested_in: 'buying',
    budget_range: '',
    preferred_contact_time: ''
  });

  useEffect(() => {
    fetchListing();
  }, [id]);

  useEffect(() => {
    if (listing?.expires_at) {
      const timer = setInterval(() => {
        const remaining = calculateTimeRemaining(listing.expires_at);
        setTimeRemaining(remaining);
        if (remaining.total <= 0) {
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [listing?.expires_at]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/p2p/listing/${id}`);
      setListing(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Listing not found');
        navigate('/deals');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeRemaining = (expiresAt) => {
    const end = new Date(expiresAt);
    const now = new Date();
    const total = end - now;

    if (total <= 0) {
      return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      total,
      days: Math.floor(total / (1000 * 60 * 60 * 24)),
      hours: Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((total % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((total % (1000 * 60)) / 1000)
    };
  };

  const handleShare = async () => {
    // Use share API endpoint for proper OG tags on social media
    const url = `${BACKEND_URL}/api/share/listing/${id}`;
    if (navigator.share) {
      try {
        await navigator.share({ 
          title: listing?.title, 
          text: listing?.description?.substring(0, 100),
          url 
        });
      } catch (err) {
        copyShareLink(url);
      }
    } else {
      copyShareLink(url);
    }
    api.post(`/p2p/listing/${id}/share`);
  };
  
  const copyShareLink = (url) => {
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Get OG image for social sharing
  const getOgImage = () => {
    if (listing?.images && listing.images.length > 0) {
      const img = listing.images[0];
      if (img.startsWith('http')) return img;
      return `${BACKEND_URL}${img}`;
    }
    return `${BACKEND_URL}/static/og-default.png`;
  };

  const handleWhatsApp = () => {
    if (!listing?.contact_whatsapp) return;
    const message = `Hi, I'm interested in your listing: ${listing.title}`;
    const url = `https://wa.me/${listing.contact_whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.phone.trim()) {
      toast.error('Name and Phone are required');
      return;
    }

    setLeadSubmitting(true);
    try {
      await api.post('/leads/capture', {
        listing_id: id,
        referrer_code: referrerCode,
        ...leadForm
      });
      setLeadSubmitted(true);
      toast.success('Your inquiry has been sent! The seller will contact you soon.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry');
    } finally {
      setLeadSubmitting(false);
    }
  };

  const nextImage = () => {
    if (listing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary/20 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-muted-foreground animate-pulse">Loading property...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
        <div className="text-center glass-card p-12 max-w-md mx-4">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <Package className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Listing Not Found</h2>
          <p className="text-muted-foreground mb-6">This property may have been sold or removed.</p>
          <Link 
            to="/deals" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all hover:scale-105"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  const isExpired = listing.status === 'expired' || (timeRemaining && timeRemaining.total <= 0);
  const isRealEstate = !!listing.real_estate_details;

  return (
    <>
      {/* OG Meta Tags for Social Sharing */}
      {listing && (
        <Helmet>
          <title>{listing.title} | P2P Marketplace - ANANT</title>
          <meta name="description" content={listing.description?.substring(0, 160)} />
          
          {/* Open Graph / Facebook / WhatsApp */}
          <meta property="og:type" content="product" />
          <meta property="og:url" content={`${window.location.origin}/listing/${id}`} />
          <meta property="og:title" content={listing.title} />
          <meta property="og:description" content={listing.description?.substring(0, 160)} />
          <meta property="og:image" content={getOgImage()} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={listing.title} />
          <meta property="product:price:amount" content={listing.price} />
          <meta property="product:price:currency" content="INR" />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={listing.title} />
          <meta name="twitter:description" content={listing.description?.substring(0, 160)} />
          <meta name="twitter:image" content={getOgImage()} />
        </Helmet>
      )}
      
      <div className="min-h-screen bg-background relative overflow-hidden" data-testid="listing-detail-page">
      {/* Ambient background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[120px]" />
      </div>
      
      {isRealEstate && <FloatingParticles />}

      {/* Premium Sticky Header */}
      <div className="border-b border-white/5 bg-background/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link 
              to="/deals" 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
            >
              <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="hidden sm:inline">Back to Marketplace</span>
              <span className="sm:hidden">Back</span>
            </Link>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsLiked(!isLiked)}
                className={`p-2.5 rounded-full transition-all ${
                  isLiked 
                    ? 'bg-red-500/20 text-red-500' 
                    : 'bg-white/5 text-muted-foreground hover:bg-white/10'
                }`}
              >
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              </button>
              <button
                onClick={handleShare}
                className="p-2.5 bg-white/5 rounded-full text-muted-foreground hover:bg-white/10 transition-colors"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Premium Image Gallery */}
            <div 
              ref={imageContainerRef}
              className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 shadow-2xl"
            >
              <div className="relative aspect-[16/10] bg-black/50 group">
                {listing.images?.length > 0 ? (
                  <>
                    <img
                      src={getImageUrl(listing.images[currentImageIndex])}
                      alt={listing.title}
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02] cursor-pointer"
                      onClick={() => setShowLightbox(true)}
                    />
                    
                    {/* Gradient overlays */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none" />
                    
                    {listing.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/80 transition-all hover:scale-110 border border-white/10"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/80 transition-all hover:scale-110 border border-white/10"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                        
                        {/* Image counter */}
                        <div className="absolute bottom-4 right-4 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full text-sm font-medium border border-white/10">
                          <ImageIcon className="w-4 h-4 inline mr-2" />
                          {currentImageIndex + 1} / {listing.images.length}
                        </div>
                        
                        {/* Progress dots */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {listing.images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`transition-all duration-300 ${
                                idx === currentImageIndex 
                                  ? 'w-8 h-2 bg-primary rounded-full' 
                                  : 'w-2 h-2 bg-white/40 rounded-full hover:bg-white/60'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
                    <div className="text-center">
                      <Package className="w-20 h-20 mx-auto text-muted-foreground/50" />
                      <p className="mt-2 text-muted-foreground">No images available</p>
                    </div>
                  </div>
                )}

                {/* Premium Status Badges */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span className={`px-4 py-2 rounded-full text-sm font-bold backdrop-blur-sm border ${
                    listing.listing_type === 'sell' 
                      ? 'bg-emerald-500/90 border-emerald-400/30 text-white shadow-lg shadow-emerald-500/20' 
                      : 'bg-blue-500/90 border-blue-400/30 text-white shadow-lg shadow-blue-500/20'
                  }`}>
                    {listing.listing_type === 'sell' ? 'For Sale' : 'Wanted'}
                  </span>
                  {listing.is_priority && (
                    <span className="px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/20 flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      Featured
                    </span>
                  )}
                  {isExpired && (
                    <span className="px-4 py-2 rounded-full text-sm font-bold bg-red-500/90 text-white">
                      Expired
                    </span>
                  )}
                  {isRealEstate && (
                    <span className="px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/20 flex items-center gap-1.5">
                      <Home className="w-3.5 h-3.5" />
                      Property
                    </span>
                  )}
                </div>
              </div>

              {/* Enhanced Thumbnails */}
              {listing.images?.length > 1 && (
                <div className="p-4 bg-black/30 backdrop-blur-sm">
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20">
                    {listing.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`flex-shrink-0 w-24 h-20 rounded-xl overflow-hidden transition-all duration-300 ${
                          idx === currentImageIndex 
                            ? 'ring-2 ring-primary ring-offset-2 ring-offset-black/50 scale-105' 
                            : 'opacity-60 hover:opacity-100 hover:scale-102'
                        }`}
                      >
                        <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Video Section - Enhanced */}
            {listing.video_url && (
              <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2 text-lg">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white fill-current" />
                  </div>
                  <span>Property Video Tour</span>
                </h3>
                <div className="aspect-video bg-black rounded-2xl overflow-hidden ring-1 ring-white/10">
                  {listing.video_url.includes('youtube.com') || listing.video_url.includes('youtu.be') ? (
                    <iframe
                      src={listing.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      title="Product Video"
                    />
                  ) : (
                    <video
                      src={listing.video_url}
                      controls
                      className="w-full h-full"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Premium Timer */}
            {listing.expires_at && timeRemaining && (
              <div className={`rounded-3xl p-6 relative overflow-hidden ${
                isExpired 
                  ? 'bg-gradient-to-r from-red-500/10 to-red-500/5 border border-red-500/30' 
                  : 'bg-gradient-to-r from-amber-500/10 to-orange-500/5 border border-amber-500/30'
              }`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full blur-2xl" />
                
                <div className="relative flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isExpired ? 'bg-red-500/20' : 'bg-amber-500/20'
                  }`}>
                    <Clock className={`w-6 h-6 ${isExpired ? 'text-red-500' : 'text-amber-500'}`} />
                  </div>
                  <div>
                    <span className="font-bold text-lg">
                      {isExpired ? 'This listing has expired' : 'Limited Time Offer'}
                    </span>
                    {!isExpired && (
                      <p className="text-sm text-muted-foreground">Hurry! This deal won't last forever</p>
                    )}
                  </div>
                </div>
                
                {!isExpired && (
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: 'Days', value: timeRemaining.days },
                      { label: 'Hours', value: timeRemaining.hours },
                      { label: 'Minutes', value: timeRemaining.minutes },
                      { label: 'Seconds', value: timeRemaining.seconds }
                    ].map(({ label, value }) => (
                      <div key={label} className="relative group">
                        <div className="text-center p-4 bg-black/30 backdrop-blur-sm rounded-2xl border border-white/10 group-hover:border-amber-500/30 transition-colors">
                          <div className="text-3xl md:text-4xl font-bold font-mono bg-gradient-to-br from-amber-400 to-orange-500 bg-clip-text text-transparent">
                            {String(value).padStart(2, '0')}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Description Section */}
            <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 p-6 md:p-8">
              {/* Title with premium styling */}
              <div className="mb-6">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight">{listing.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-full text-sm">
                    <Eye className="w-4 h-4 text-primary" />
                    {listing.views} views
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-full text-sm">
                    <MapPin className="w-4 h-4 text-cyan-500" />
                    {listing.location_city}, {listing.location_state}
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-full text-sm">
                    <Calendar className="w-4 h-4 text-purple-500" />
                    {new Date(listing.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Description with nice formatting */}
              <div className="mb-8">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Description
                </h3>
                <div className="p-4 bg-black/20 rounded-2xl border border-white/5">
                  <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{listing.description}</p>
                </div>
              </div>

              {/* Quick Info Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-5 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-2xl border border-emerald-500/20">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Condition</p>
                  <p className="font-bold text-lg capitalize flex items-center gap-2">
                    <BadgeCheck className="w-5 h-5 text-emerald-500" />
                    {listing.condition?.replace('_', ' ')}
                  </p>
                </div>
                <div className="p-5 bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-2xl border border-purple-500/20">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Category</p>
                  <p className="font-bold text-lg flex items-center gap-2">
                    <Tag className="w-5 h-5 text-purple-500" />
                    {listing.category?.name || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Real Estate Details Section - Premium Design */}
              {listing.real_estate_details && (
                <div className="mt-8 space-y-6" data-testid="real-estate-details">
                  {/* Property Overview Card */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-6">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
                    <h3 className="text-xl font-bold mb-5 flex items-center gap-3 relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center shadow-lg shadow-primary/30">
                        <Home className="w-6 h-6 text-white" />
                      </div>
                      <span>Property Details</span>
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 relative">
                      {listing.real_estate_details.property_type && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Property Type</p>
                          <p className="font-bold text-lg capitalize mt-1">{listing.real_estate_details.property_type.replace('_', ' ')}</p>
                        </div>
                      )}
                      {listing.real_estate_details.bhk && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Configuration</p>
                          <p className="font-bold text-lg mt-1">{listing.real_estate_details.bhk}</p>
                        </div>
                      )}
                      {listing.real_estate_details.furnishing && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Furnishing</p>
                          <p className="font-bold text-lg capitalize mt-1">{listing.real_estate_details.furnishing.replace('_', ' ')}</p>
                        </div>
                      )}
                      {listing.real_estate_details.possession_status && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Possession</p>
                          <p className="font-bold text-lg capitalize mt-1">{listing.real_estate_details.possession_status.replace('_', ' ')}</p>
                        </div>
                      )}
                      {listing.real_estate_details.ownership_type && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Ownership</p>
                          <p className="font-bold text-lg capitalize mt-1">{listing.real_estate_details.ownership_type.replace('_', ' ')}</p>
                        </div>
                      )}
                      {listing.real_estate_details.age_of_property && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5 hover:border-primary/30 transition-colors">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Property Age</p>
                          <p className="font-bold text-lg capitalize mt-1">{listing.real_estate_details.age_of_property.replace('_', ' ')}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Area & Location - Cyan Theme */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500/10 via-cyan-500/5 to-transparent border border-cyan-500/20 p-6">
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl" />
                    <h3 className="text-xl font-bold mb-5 flex items-center gap-3 relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                        <Ruler className="w-6 h-6 text-white" />
                      </div>
                      <span>Area & Location</span>
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative">
                      {listing.real_estate_details.carpet_area && (
                        <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20 hover:bg-cyan-500/20 transition-colors">
                          <p className="text-xs text-cyan-300 uppercase tracking-wider">Carpet Area</p>
                          <p className="font-bold text-2xl text-cyan-400 mt-1">{listing.real_estate_details.carpet_area}</p>
                          <p className="text-xs text-muted-foreground">sq.ft</p>
                        </div>
                      )}
                      {listing.real_estate_details.built_up_area && (
                        <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20 hover:bg-blue-500/20 transition-colors">
                          <p className="text-xs text-blue-300 uppercase tracking-wider">Built-up Area</p>
                          <p className="font-bold text-2xl text-blue-400 mt-1">{listing.real_estate_details.built_up_area}</p>
                          <p className="text-xs text-muted-foreground">sq.ft</p>
                        </div>
                      )}
                      {listing.real_estate_details.super_built_up_area && (
                        <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/20 hover:bg-purple-500/20 transition-colors">
                          <p className="text-xs text-purple-300 uppercase tracking-wider">Super Built-up</p>
                          <p className="font-bold text-2xl text-purple-400 mt-1">{listing.real_estate_details.super_built_up_area}</p>
                          <p className="text-xs text-muted-foreground">sq.ft</p>
                        </div>
                      )}
                      {listing.real_estate_details.plot_area && (
                        <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors">
                          <p className="text-xs text-emerald-300 uppercase tracking-wider">Plot Area</p>
                          <p className="font-bold text-2xl text-emerald-400 mt-1">{listing.real_estate_details.plot_area}</p>
                          <p className="text-xs text-muted-foreground">sq.ft</p>
                        </div>
                      )}
                      {listing.real_estate_details.floor_number && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Floor</p>
                          <p className="font-bold text-lg mt-1">{listing.real_estate_details.floor_number}{listing.real_estate_details.total_floors ? ` of ${listing.real_estate_details.total_floors}` : ''}</p>
                        </div>
                      )}
                      {listing.real_estate_details.facing && (
                        <div className="p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                            <Compass className="w-3 h-3" /> Facing
                          </p>
                          <p className="font-bold text-lg capitalize mt-1">{listing.real_estate_details.facing.replace('_', '-')}</p>
                        </div>
                      )}
                    </div>

                    {/* Full Address */}
                    {(listing.real_estate_details.locality || listing.real_estate_details.landmark) && (
                      <div className="mt-4 p-4 bg-black/20 backdrop-blur-sm rounded-xl border border-white/5">
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                          <MapPin className="w-3 h-3 text-cyan-500" /> Location Details
                        </p>
                        <p className="font-semibold text-lg">
                          {listing.real_estate_details.locality}
                          {listing.real_estate_details.landmark && (
                            <span className="text-muted-foreground font-normal"> (Near {listing.real_estate_details.landmark})</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Amenities - Green Theme */}
                  {listing.real_estate_details.amenities?.length > 0 && (
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/20 p-6">
                      <div className="absolute top-0 left-1/2 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl" />
                      <h3 className="text-xl font-bold mb-5 flex items-center gap-3 relative">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                          <Shield className="w-6 h-6 text-white" />
                        </div>
                        <span>Amenities</span>
                        <span className="ml-2 px-3 py-1 bg-emerald-500/20 rounded-full text-sm text-emerald-400">{listing.real_estate_details.amenities.length} available</span>
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 relative">
                        {listing.real_estate_details.amenities.map((amenity, idx) => {
                          const IconComponent = amenityIcons[amenity] || Check;
                          return (
                            <div 
                              key={idx} 
                              className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20 hover:bg-emerald-500/20 hover:scale-[1.02] transition-all cursor-default"
                            >
                              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                <IconComponent className="w-4 h-4 text-emerald-400" />
                              </div>
                              <span className="text-sm font-medium capitalize">{amenity.replace(/_/g, ' ')}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Price Breakdown - Primary Theme */}
                  {(listing.real_estate_details.price_per_sqft || listing.real_estate_details.maintenance_charges || listing.real_estate_details.booking_amount) && (
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-amber-500/5 to-transparent border border-primary/20 p-6">
                      <h3 className="text-xl font-bold mb-5 flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center shadow-lg shadow-primary/30">
                          <IndianRupee className="w-6 h-6 text-white" />
                        </div>
                        <span>Price Breakdown</span>
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {listing.real_estate_details.price_per_sqft && (
                          <div className="p-5 bg-primary/10 rounded-xl border border-primary/20">
                            <p className="text-xs text-primary/70 uppercase tracking-wider">Price per sq.ft</p>
                            <p className="font-bold text-3xl bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent mt-1">
                              ₹{listing.real_estate_details.price_per_sqft.toLocaleString()}
                            </p>
                          </div>
                        )}
                        {listing.real_estate_details.maintenance_charges && (
                          <div className="p-5 bg-black/20 rounded-xl border border-white/5">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Maintenance/month</p>
                            <p className="font-bold text-2xl mt-1">₹{listing.real_estate_details.maintenance_charges.toLocaleString()}</p>
                          </div>
                        )}
                        {listing.real_estate_details.booking_amount && (
                          <div className="p-5 bg-black/20 rounded-xl border border-white/5">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Booking Amount</p>
                            <p className="font-bold text-2xl mt-1">₹{listing.real_estate_details.booking_amount.toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* RERA Badge - Premium */}
                  {(listing.real_estate_details.rera_registered || listing.real_estate_details.rera_number) && (
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-transparent border-2 border-emerald-500/30 p-6">
                      <div className="absolute -right-10 -top-10 w-32 h-32">
                        <div className="w-full h-full rounded-full bg-emerald-500/20 blur-2xl" />
                      </div>
                      <div className="flex items-center gap-4 relative">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                          <Shield className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-emerald-400 flex items-center gap-2">
                            <BadgeCheck className="w-5 h-5" />
                            RERA Registered
                          </h3>
                          {listing.real_estate_details.rera_number && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-sm text-muted-foreground">RERA No:</span>
                              <span className="font-mono font-bold bg-black/30 px-4 py-1.5 rounded-lg border border-emerald-500/30">{listing.real_estate_details.rera_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Virtual Tour Link - Hyper Button */}
                  {listing.real_estate_details.virtual_tour_url && (
                    <a
                      href={listing.real_estate_details.virtual_tour_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group relative overflow-hidden rounded-2xl p-6 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 border border-cyan-500/30 hover:border-cyan-500/50 transition-all"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/10 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                      <div className="flex items-center justify-between relative">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-xl shadow-cyan-500/30 group-hover:scale-110 transition-transform">
                            <Eye className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-lg group-hover:text-cyan-400 transition-colors">View Virtual Tour</p>
                            <p className="text-sm text-muted-foreground">360° walkthrough of the property</p>
                          </div>
                        </div>
                        <ExternalLink className="w-6 h-6 text-cyan-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </div>
                    </a>
                  )}
                </div>
              )}

              {/* Custom Buttons (VIP/Premium) */}
              {listing.custom_buttons?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-3">
                  {listing.custom_buttons.map((btn, idx) => (
                    <a
                      key={idx}
                      href={btn.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {btn.label}
                    </a>
                  ))}
                </div>
              )}

              {/* External URL */}
              {listing.external_url && (
                <div className="mt-4">
                  <a
                    href={listing.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {listing.external_url}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Price & Contact - Premium Sticky Card */}
          <div className="space-y-6 lg:sticky lg:top-20 lg:self-start">
            {/* Premium Price Card */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10 shadow-2xl">
              {/* Decorative glow */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-secondary/10 rounded-full blur-3xl" />
              
              <div className="relative p-6">
                {/* Price Display */}
                <div className="text-center mb-6">
                  <p className="text-sm text-muted-foreground mb-2 uppercase tracking-wider">
                    {listing.listing_type === 'sell' ? 'Asking Price' : 'Budget'}
                  </p>
                  <div className="relative inline-block">
                    <p className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary via-amber-400 to-primary bg-clip-text text-transparent">
                      ₹{listing.price?.toLocaleString()}
                    </p>
                    <Sparkles className="absolute -top-2 -right-6 w-5 h-5 text-primary animate-pulse" />
                  </div>
                  {listing.price_negotiable && (
                    <p className="text-sm text-emerald-400 mt-2 flex items-center justify-center gap-1">
                      <Check className="w-4 h-4" />
                      Price Negotiable
                    </p>
                  )}
                </div>

                {/* Pass Tier Badge - Enhanced */}
                <div className="flex justify-center mb-6">
                  <span className={`px-5 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 ${
                    listing.pass_tier === 'PREMIUM' ? 'bg-gradient-to-r from-pink-500/20 to-rose-500/20 text-pink-400 border border-pink-500/30' :
                    listing.pass_tier === 'VIP' ? 'bg-gradient-to-r from-purple-500/20 to-violet-500/20 text-purple-400 border border-purple-500/30' :
                    listing.pass_tier === 'DIAMOND' ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30' :
                    listing.pass_tier === 'GOLD' ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border border-amber-500/30' :
                    'bg-white/5 text-gray-400 border border-white/10'
                  }`}>
                    <Star className="w-4 h-4" />
                    {listing.pass_tier} Listing
                  </span>
                </div>

                {/* Contact Info */}
                {listing.contact_hidden ? (
                  <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                      <Lock className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">Contact Hidden</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Activate your account to view seller contact details
                    </p>
                    <Link
                      to="/activation-packs"
                      className="block w-full py-4 bg-gradient-to-r from-primary to-amber-500 text-primary-foreground rounded-xl font-bold text-center hover:shadow-lg hover:shadow-primary/30 transition-all hover:scale-[1.02]"
                    >
                      Activate Now
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {listing.contact_phone && (
                      <a
                        href={`tel:${listing.contact_phone}`}
                        className="flex items-center gap-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl hover:bg-emerald-500/20 transition-all group"
                      >
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Phone className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Phone</p>
                          <p className="font-bold text-lg">{listing.contact_phone}</p>
                        </div>
                      </a>
                    )}
                    
                    {listing.contact_whatsapp && (
                      <button
                        onClick={handleWhatsApp}
                        className="w-full flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/20 rounded-2xl hover:bg-green-500/20 transition-all group"
                      >
                        <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <MessageCircle className="w-6 h-6 text-green-400" />
                        </div>
                        <div className="text-left">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">WhatsApp</p>
                          <p className="font-bold text-lg">{listing.contact_whatsapp}</p>
                        </div>
                      </button>
                    )}
                    
                    {listing.contact_email && (
                      <a
                        href={`mailto:${listing.contact_email}`}
                        className="flex items-center gap-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl hover:bg-blue-500/20 transition-all group"
                      >
                        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Mail className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">Email</p>
                          <p className="font-bold text-lg truncate max-w-[180px]">{listing.contact_email}</p>
                        </div>
                      </a>
                    )}
                  </div>
                )}

                {/* Share Button - Premium */}
                <button
                  onClick={handleShare}
                  className="w-full mt-4 flex items-center justify-center gap-2 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 group"
                >
                  <Share2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium">Share Listing</span>
                </button>
              </div>
            </div>

            {/* Seller Info - Enhanced */}
            <div className="rounded-2xl bg-gradient-to-br from-white/[0.05] to-transparent border border-white/10 p-5">
              <h3 className="font-bold mb-4 text-sm uppercase tracking-wider text-muted-foreground">Seller</h3>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center overflow-hidden">
                    {listing.seller?.profile_photo ? (
                      <img src={getImageUrl(listing.seller.profile_photo)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-7 h-7 text-primary" />
                    )}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-background">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div>
                  <p className="font-bold text-lg">{listing.seller?.name || 'Anonymous'}</p>
                  <p className="text-sm text-muted-foreground">@{listing.seller?.username}</p>
                </div>
              </div>
            </div>

            {/* Safety Tips - Modern Design */}
            <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 p-5">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Safety Tips
              </h3>
              <ul className="space-y-3">
                {[
                  'Meet in a public place for transactions',
                  'Inspect items before payment',
                  'Never share financial information'
                ].map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-muted-foreground">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Lead Capture Form - Premium Design */}
            {listing.real_estate_details && (
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-2 border-primary/30 p-6" data-testid="lead-capture-form">
                {/* Decorative elements */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-secondary/20 rounded-full blur-2xl" />
                
                <div className="relative">
                  <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center">
                      <Send className="w-5 h-5 text-white" />
                    </div>
                    <span>Get More Details</span>
                  </h3>
                  <p className="text-sm text-muted-foreground mb-5">
                    Interested? Let the seller know and get contacted!
                  </p>

                  {leadSubmitted ? (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                        <Check className="w-10 h-10 text-white" />
                      </div>
                      <h4 className="font-bold text-xl text-emerald-400 mb-2">Inquiry Sent!</h4>
                      <p className="text-sm text-muted-foreground">
                        The property owner will contact you soon.
                      </p>
                    </div>
                  ) : showLeadForm ? (
                    <form onSubmit={handleLeadSubmit} className="space-y-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Your Name *"
                          value={leadForm.name}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3.5 bg-black/30 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50"
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="tel"
                          placeholder="Phone Number *"
                          value={leadForm.phone}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-3.5 bg-black/30 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50"
                          required
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          placeholder="Email (optional)"
                          value={leadForm.email}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3.5 bg-black/30 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50"
                        />
                      </div>
                      <div>
                        <select
                          value={leadForm.interested_in}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, interested_in: e.target.value }))}
                          className="w-full px-4 py-3.5 bg-black/30 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all"
                        >
                          <option value="buying">Interested in Buying</option>
                          <option value="renting">Interested in Renting</option>
                          <option value="investing">Interested in Investing</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Your Budget Range (optional)"
                          value={leadForm.budget_range}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, budget_range: e.target.value }))}
                          className="w-full px-4 py-3.5 bg-black/30 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50"
                        />
                      </div>
                      <div>
                        <textarea
                          placeholder="Your Message (optional)"
                          value={leadForm.message}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, message: e.target.value }))}
                          className="w-full px-4 py-3.5 bg-black/30 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all h-24 resize-none placeholder:text-muted-foreground/50"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={leadSubmitting}
                        className="w-full py-4 bg-gradient-to-r from-primary to-amber-500 text-primary-foreground rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-primary/30 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                      >
                        {leadSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Send Inquiry
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowLeadForm(false)}
                        className="w-full py-3 text-sm text-muted-foreground hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button
                      onClick={() => setShowLeadForm(true)}
                      className="w-full py-4 bg-gradient-to-r from-primary to-amber-500 text-primary-foreground rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-primary/30 transition-all hover:scale-[1.02]"
                      data-testid="show-lead-form-btn"
                    >
                      <Send className="w-5 h-5" />
                      I'm Interested - Contact Me
                    </button>
                  )}

                  {referrerCode && (
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                      Referred by: <span className="text-primary font-bold">{referrerCode}</span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {showLightbox && listing.images?.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 rotate-45" />
          </button>
          <img
            src={getImageUrl(listing.images[currentImageIndex])}
            alt={listing.title}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {listing.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default ListingDetail;
